import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CalendarDays, Plus, Trash2, Eye, Users, Phone, StickyNote, Clock } from "lucide-react";

const TIPI = [
  { v: "controllo_vista", l: "Controllo vista", icon: Eye, tone: "bg-[#1E40AF]/10 text-[#1E40AF]", dot: "bg-[#1E40AF]" },
  { v: "rappresentante", l: "Appuntamento rappresentante", icon: Users, tone: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  { v: "call", l: "Call", icon: Phone, tone: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  { v: "altro", l: "Nota / Altro", icon: StickyNote, tone: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
];
const TIPO_MAP = Object.fromEntries(TIPI.map((t) => [t.v, t]));

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromKey = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const EMPTY = { titolo: "", tipo: "controllo_vista", ora: "", note: "" };

export default function AgendaCalendar() {
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/eventi");
    setEvents(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selKey = toKey(selected);
  const eventDates = events.map((e) => fromKey(e.data));
  const dayEvents = events.filter((e) => e.data === selKey).sort((a, b) => (a.ora || "").localeCompare(b.ora || ""));

  const add = async () => {
    if (!form.titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      await api.post("/eventi", { ...form, data: selKey, ora: form.ora || null, note: form.note || null });
      toast.success("Evento aggiunto");
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/eventi/${id}`);
      toast.success("Evento eliminato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const prettyDay = selected.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="agenda-calendar">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-[#1E40AF]" />
        <h2 className="font-head text-lg font-semibold text-slate-900">Agenda</h2>
        <span className="ml-auto text-xs text-slate-400">Controlli, rappresentanti, call e note</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="p-4 border-b lg:border-b-0 lg:border-r border-slate-200 flex justify-center">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ hasEvent: eventDates }}
            modifiersClassNames={{ hasEvent: "bg-[#1E40AF]/10 text-[#1E40AF] font-bold rounded-md" }}
            data-testid="agenda-calendar-picker"
          />
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm font-semibold text-slate-900 capitalize">{prettyDay}</div>

          {/* Add form */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input data-testid="evento-titolo" value={form.titolo} onChange={(e) => set("titolo", e.target.value)} placeholder="Titolo evento…" className="h-9 bg-white" />
              </div>
              <div className="relative">
                <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input data-testid="evento-ora" type="time" value={form.ora} onChange={(e) => set("ora", e.target.value)} className="h-9 pl-7 bg-white" />
              </div>
            </div>
            <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="evento-tipo" className="h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPI.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="evento-note" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Note (facoltative)…" rows={2} className="bg-white" />
            <Button data-testid="evento-add-btn" onClick={add} disabled={saving} size="sm" className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi al {selected.toLocaleDateString("it-IT")}
            </Button>
          </div>

          {/* Day events */}
          <div className="space-y-2 max-h-56 overflow-auto custom-scroll">
            {dayEvents.length === 0 && <div className="text-sm text-slate-400 text-center py-4">Nessun evento in questa giornata.</div>}
            {dayEvents.map((e) => {
              const t = TIPO_MAP[e.tipo] || TIPO_MAP.altro;
              const Icon = t.icon;
              return (
                <div key={e.id} data-testid={`evento-${e.id}`} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 group">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${t.tone}`}><Icon className="h-4 w-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {e.ora && <span className="text-xs font-mono font-semibold text-slate-700">{e.ora}</span>}
                      <span className="text-sm font-medium text-slate-900 truncate">{e.titolo}</span>
                    </div>
                    <div className="text-xs text-slate-500">{t.l}{e.note ? ` · ${e.note}` : ""}</div>
                  </div>
                  <button data-testid={`evento-delete-${e.id}`} onClick={() => del(e.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
